import { ApolloQueryResult } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import get from 'lodash/get';
import set from 'lodash/set';
import * as React from 'react';
import { setPricingEligibility } from '@atlassian/cssxp-types-billing';
import { CommerceAddress } from '@css-xp/graphtypes';
import {
  useSidebar,
  useFormSubmit,
  useNotification,
  ErrorComponents,
  InfoPanel,
  AddressFormSection,
  successNotificationBuilder,
} from '@css-xp/base';

import { updatePricingEligibility } from '@atlassian/temp-proxies';
import ChangePricingEligibility from '../component/update-pricing-eligibility';

/**
 * This component is required to pass closeSidebar to the form. calling useSidebar
 * directly on the calling page causes continuous re-rendering
 * */

interface FormData {
  pricingEligibility?: string;
}

export interface Props {
  transactionAccountId: string;
  pricingEligibility: string | null;
  billToAddress: CommerceAddress;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}
const SetPricingEligibilitySidebar = (props: Props) => {
  const { closeSidebar } = useSidebar();

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<setPricingEligibility, any>({
    submitFormAction: updatePricingEligibility,
    onComplete: () => {
      closeSidebar();
      if (props.refetch) {
        props.refetch();
      }
    },
  });
  const submitEditBillToPartyDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });

    const updateBillToPartyPayload: setPricingEligibility = {
      pricingEligibility: {
        [get(transformedData, 'pricingPlan')]: 'true',
      },
      transactionAccountId: props.transactionAccountId,
    };
    submitForm(updateBillToPartyPayload);
  };

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('update-pricing-eligibility-success')
      .withMessage('Successfully set Pricing Eligibility.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  return (
    <div style={{ maxWidth: '500px', padding: '25px' }}>
      <Form onSubmit={submitEditBillToPartyDetails}>
        {({ formProps, setFieldValue, submitting }) => (
          <form {...formProps}>
            <FormHeader title="Set Pricing Eligibility"></FormHeader>
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="Unable to set pricing eligibility due to the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <ChangePricingEligibility {...props} />
            <FormSection>
              <h3>Bill to address</h3>
              <AddressFormSection
                {...props.billToAddress}
                isEditable={false}
                addressName="Bill to"
                setFieldValue={setFieldValue}
                isRequired={false}
              />
            </FormSection>
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  isDisabled={false}
                  testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                >
                  Save
                </LoadingButton>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
export default SetPricingEligibilitySidebar;
